import {
  DataFilterField, DataFilterOption,
} from '../../../shared/ui/data-filter/data-filter.component';
import { StageCatalogEntry } from '../../../shared/stage-catalog';
import type { AssignableRep } from '../board/leads-board.component';
import { RollOffContainerType } from './rolloff-opportunity.model';

/**
 * The columns a roll-off hire can be narrowed by, for the shared filter bar -
 * on the Board and on the Table view alike.
 *
 * A function rather than a constant because three columns' options arrive
 * late: the container sizes, the stages and the roster are each read from the
 * server, and a bar built before they land would offer a dropdown with nothing
 * in it. Declared here rather than on either view so both carry the same bar
 * by calling the same function - two lists written by hand beside each other
 * is how one of them stops matching the server.
 *
 * The ids are the table's own column ids, which is what lets a header and the
 * filter agree about which column is being talked about - and what the server
 * whitelist (RollOffFilterFields) is written against. A contract test holds
 * the two together.
 *
 * Stage and sales rep are here although the board has a control for each -
 * the rail focuses a stage, the chip narrows to the signed-in rep. The Table
 * view has neither, and a column it shows but cannot narrow is the gap this
 * bar exists to close. They are settled the way the Leads list settles the
 * same pairs: a stage rule ANDs with the rail's focus, and the board leaves
 * the rep rule out while "Assigned to me" is on. Unpinned, all of them: the
 * board's chrome already carries a rail, a search, a chip and a switch.
 */
export function rollOffRuleFilterFields(
  containerTypes: readonly RollOffContainerType[],
  stages: readonly StageCatalogEntry[],
  reps: readonly AssignableRep[],
): DataFilterField[] {
  const none: readonly DataFilterOption[] = [];
  return [
    { id: 'siteName', label: 'Site', kind: 'text', pinned: false, options: none },
    { id: 'address', label: 'Address', kind: 'text', pinned: false, options: none },
    { id: 'city', label: 'City', kind: 'text', pinned: false, options: none },
    { id: 'postalCode', label: 'ZIP', kind: 'text', pinned: false, options: none },
    {
      id: 'stage', label: 'Stage', pinned: false, allLabel: 'Any stage',
      // By CODE, for the reason the container is: the name is the tenant's
      // wording, and renaming a stage must not retire a link that names it.
      options: stages.map(stage => ({ value: stage.code, label: stage.name })),
    },
    {
      id: 'container', label: 'Container', pinned: false, allLabel: 'Any size',
      // Value is the type's CODE, which is what the server filters on; the
      // label is the tenant's own wording for it. Never the label as value: a
      // tenant renaming "8 yd" would silently retire every link naming it.
      options: containerTypes.map(type => ({ value: type.code, label: type.label })),
    },
    { id: 'quotedPrice', label: 'Quoted', kind: 'number', pinned: false, options: none },
    { id: 'hireStartsOn', label: 'Starts', kind: 'date', pinned: false, options: none },
    { id: 'hireEndsOn', label: 'Ends', kind: 'date', pinned: false, options: none },
    {
      id: 'salesRep', label: 'Sales rep', pinned: false, allLabel: 'Any sales rep',
      // "Unassigned" is a value, not the absence of one: the hires nobody holds
      // are a question of their own, and the server reads anything that is not
      // a user id as that. A rep is named the way a card and a row name one.
      options: [
        { value: 'unassigned', label: 'Unassigned' },
        ...reps.map(rep => ({
          value: rep.userId,
          label: rep.displayName?.trim() || rep.userName?.trim() || `Rep ${rep.userId}`,
        })),
      ],
    },
    { id: 'createdAt', label: 'Created', kind: 'date', pinned: false, options: none },
  ];
}
